import React, {
    FC,
    MouseEvent as ReactMouseEvent,
    ReactNode,
    useLayoutEffect,
    useState,
} from 'react';
import classnames from 'classnames';
import { toSeconds } from '@yandex-int/ims.opaque-types/lib';

import { usePlayerInstance } from '@/scenarios/legacy-player/lib/contexts/instance';

import { CursorTime } from '../cursor-time/component';
import { Time } from '../../types/time';
import { useDuration } from '../../hooks/use-duration';
import { useCurrentTime } from '../../hooks/use-current-time';

import { checkIsDistanceBetweenMarkersSufficient } from './utils';
import styles from './styles.module.css';

interface Props {
    isDragged: boolean;
    timelineWidth?: number;
    containerLeftPadding?: number;
    className?: string;
    renderCursor?: (time: Time) => ReactNode;
}

const CURSOR_PADDING = 1;

export const HoverBar: FC<Props> = ({
    className,
    timelineWidth,
    containerLeftPadding = 0,
    isDragged,
    renderCursor = (time) => <CursorTime time={time} />,
}) => {
    const instance = usePlayerInstance();
    const duration = useDuration();
    const currentTime = useCurrentTime();
    const [cursorPositionOnTimeline, setCursorPositionOnTimeline] = useState<number>();

    const seekToPoint = (event: ReactMouseEvent<HTMLButtonElement>) => {
        if (instance && timelineWidth) {
            instance.seek(
                toSeconds(
                    duration.getTimeFromPercent(event.nativeEvent.offsetX / timelineWidth)
                        .streamTime
                )
            );
        }
    };

    useLayoutEffect(() => {
        const onMouseMove = (event: MouseEvent) => {
            const isMovementOnTimeline =
                event.clientX <= (timelineWidth || 0) + containerLeftPadding &&
                event.clientX >= containerLeftPadding;

            if (isMovementOnTimeline) {
                setCursorPositionOnTimeline(event.clientX - containerLeftPadding);
            }
        };

        document.addEventListener('mousemove', onMouseMove);

        return () => document.removeEventListener('mousemove', onMouseMove);
    }, [containerLeftPadding, timelineWidth]);

    if (!cursorPositionOnTimeline || !timelineWidth) {
        return null;
    }

    const isCurrentTimeVisible =
        !isDragged &&
        checkIsDistanceBetweenMarkersSufficient({
            cursorPositionOnTimeline,
            currentTime,
            timelineWidth,
            duration,
        });

    return (
        <div className={classnames(styles.root, className)}>
            <button
                onClick={seekToPoint}
                className={styles.hoverBar}
                style={{
                    width: cursorPositionOnTimeline + CURSOR_PADDING,
                    maxWidth: timelineWidth,
                }}
            />
            {isCurrentTimeVisible && (
                <div
                    className={styles.cursor}
                    style={{
                        left: `${duration.getPercentFromTime(currentTime) * 100}%`,
                    }}
                >
                    <CursorTime time={currentTime} />
                </div>
            )}
            <div className={styles.cursor} style={{ left: cursorPositionOnTimeline }}>
                {renderCursor(
                    duration.getTimeFromPercent(cursorPositionOnTimeline / timelineWidth)
                )}
            </div>
        </div>
    );
};
